<template>
  <div class="row">
    <div class="col-md-12">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Árvore de Grupos de Manual</h5>
          <div>
            <b-button variant="success" class="mr-2" @click="openCreateRoot">
              <i class="fas fa-plus mr-1"></i>
              Novo Grupo Pai
            </b-button>
            <b-button variant="outline-secondary" class="mr-2" @click="$router.push('/manual-grupos')">
              Voltar
            </b-button>
            <b-button variant="primary" @click="loadTree">
              Atualizar
            </b-button>
          </div>
        </div>

        <div v-if="loading" class="text-center py-3">
          <i class="fas fa-spinner fa-spin mr-1"></i>
          Carregando árvore...
        </div>

        <div v-else-if="!tree.length" class="text-muted">
          Nenhum grupo cadastrado.
        </div>

        <ul v-else class="tree-root">
          <manual-tree-node
              v-for="node in tree"
              :key="node.id"
              :node="node"
              @add-child="openCreateChild"
              @edit="openEditNode"
              @delete="confirmDeleteNode"
          />
        </ul>
      </b-card>

      <b-modal
          v-model="showEditor"
          :title="isEdit ? 'Editar Grupo' : 'Novo Grupo'"
          centered
          hide-footer
      >
        <b-form @submit.prevent="saveNode">
          <b-form-group label="Nome" label-for="node_nome">
            <b-form-input
                id="node_nome"
                v-model="form.nome"
                placeholder="Digite o nome do grupo"
                required
            />
          </b-form-group>

          <b-form-group label="Grupo Pai (opcional)" label-for="node_parent">
            <v-select
                id="node_parent"
                v-model="form.parent_id"
              :options="editorParentOptions"
                :label="'path'"
                :reduce="option => option.id"
                :clearable="true"
                placeholder="Selecione o grupo pai"
            />
          </b-form-group>

          <b-form-group label="Status" label-for="node_status">
            <b-form-select
                id="node_status"
                v-model="form.status"
                :options="statusOptions"
            />
          </b-form-group>

          <div class="d-flex justify-content-end">
            <b-button variant="outline-secondary" class="mr-2" @click="showEditor = false">
              Cancelar
            </b-button>
            <b-button variant="primary" type="submit" :disabled="isSaving || !form.nome">
              <span v-if="isSaving"><i class="fas fa-spinner fa-spin mr-1"></i>Salvando...</span>
              <span v-else>Salvar</span>
            </b-button>
          </div>
        </b-form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import vSelect from 'vue-select';
import { BCard, BButton, BModal, BForm, BFormGroup, BFormInput, BFormSelect } from 'bootstrap-vue';
import ManualTreeNode from './components/ManualTreeNode.vue';

export default {
  name: 'ManualGrupoTree',
  components: {
    vSelect,
    BCard,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    ManualTreeNode,
  },
  data() {
    return {
      loading: false,
      isSaving: false,
      tree: [],
      parentOptions: [],
      showEditor: false,
      isEdit: false,
      editingNodeId: null,
      form: {
        nome: '',
        parent_id: null,
        status: 1,
      },
      statusOptions: [
        { value: 1, text: 'Ativo' },
        { value: 0, text: 'Inativo' },
      ],
    };
  },
  mounted() {
    this.loadTree();
    this.loadParentOptions();
  },
  computed: {
    editorParentOptions() {
      if (!this.isEdit || !this.editingNodeId) {
        return this.parentOptions;
      }

      return this.parentOptions.filter(option => option.id !== this.editingNodeId);
    },
  },
  methods: {
    async loadTree() {
      this.loading = true;
      try {
        const response = await axios.get(this.$store.state.api + 'manual-grupos/tree');
        const data = response.data.data || response.data || [];
        const normalizedTree = Array.isArray(data) ? data : [];
        this.tree = await this.enrichTreeWithManuals(normalizedTree);
      } catch (e) {
        this.tree = [];
        this.$message('Erro', 'Não foi possível carregar a árvore de grupos', 'error');
      } finally {
        this.loading = false;
      }
    },

    collectNodeIds(nodes) {
      const ids = [];

      const walk = list => {
        (list || []).forEach(node => {
          if (node && node.id) {
            ids.push(node.id);
          }
          if (node && Array.isArray(node.children) && node.children.length) {
            walk(node.children);
          }
        });
      };

      walk(nodes);
      return ids;
    },

    async enrichTreeWithManuals(nodes) {
      const ids = this.collectNodeIds(nodes);
      if (!ids.length) {
        return nodes;
      }

      const responses = await Promise.all(ids.map(id =>
        axios
            .get(this.$store.state.api + `manual-grupos/${id}/manuais`)
            .then(response => ({ id, manuals: response.data.data || [] }))
            .catch(() => ({ id, manuals: [] }))
      ));

      const manualsMap = responses.reduce((acc, item) => {
        acc[item.id] = Array.isArray(item.manuals) ? item.manuals : [];
        return acc;
      }, {});

      const apply = list => (list || []).map(node => {
        const children = Array.isArray(node.children) ? apply(node.children) : [];
        const fromNode = Array.isArray(node.manuais)
            ? node.manuais
            : (Array.isArray(node.manuals) ? node.manuals : []);
        const manuals = fromNode.length ? fromNode : (manualsMap[node.id] || []);

        return {
          ...node,
          manuais: manuals,
          manuais_count: manuals.length,
          children,
        };
      });

      return apply(nodes);
    },

    loadParentOptions() {
      return axios
          .get(this.$store.state.api + 'manual-grupos/options')
          .then(response => {
            const data = response.data.data || response.data || [];
            this.parentOptions = Array.isArray(data) ? data : [];
          })
          .catch(() => {
            this.parentOptions = [];
          });
    },

    resetForm() {
      this.form = {
        nome: '',
        parent_id: null,
        status: 1,
      };
      this.isEdit = false;
      this.editingNodeId = null;
    },

    openCreateRoot() {
      this.resetForm();
      this.showEditor = true;
    },

    openCreateChild(node) {
      this.resetForm();
      this.form.parent_id = node.id;
      this.showEditor = true;
    },

    openEditNode(node) {
      this.resetForm();
      this.isEdit = true;
      this.editingNodeId = node.id;
      this.form.nome = node.nome || '';
      this.form.parent_id = node.parent_id || null;
      this.form.status = node.status ? 1 : 0;
      this.showEditor = true;
    },

    saveNode() {
      this.isSaving = true;

      let api = this.$store.state.api + 'manual-grupos';
      let method = 'post';

      if (this.isEdit && this.editingNodeId) {
        api += '/' + this.editingNodeId;
        method = 'put';
      }

      axios[method](api, this.form)
          .then(() => {
            this.$message('Sucesso', 'Grupo salvo com sucesso', 'success');
            this.showEditor = false;
            this.resetForm();
            return Promise.all([this.loadTree(), this.loadParentOptions()]);
          })
          .catch(error => {
            const message = error?.response?.data?.message || 'Erro ao salvar grupo';
            this.$message('Erro', message, 'error');
          })
          .finally(() => {
            this.isSaving = false;
          });
    },

    confirmDeleteNode(node) {
      this.$bvModal
          .msgBoxConfirm(`Deseja excluir o grupo '${node.nome}'?`, {
            title: 'Confirmar exclusão',
            centered: true,
            okVariant: 'danger',
            okTitle: 'Excluir',
            cancelTitle: 'Cancelar',
          })
          .then(confirmed => {
            if (!confirmed) return;

            return axios.delete(this.$store.state.api + 'manual-grupos/' + node.id)
                .then(() => {
                  this.$message('Sucesso', 'Grupo excluído com sucesso', 'success');
                  return Promise.all([this.loadTree(), this.loadParentOptions()]);
                })
                .catch(error => {
                  const message = error?.response?.data?.message || 'Erro ao excluir grupo';
                  this.$message('Erro', message, 'error');
                });
          });
    },
  },
};
</script>

<style scoped>
.tree-root {
  list-style: none;
  padding-left: 20px;
  margin: 0;
}
</style>
